import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import personasConfig from "../../configs/personas.json";

const TEMPLATES = personasConfig.templates;

/**
 * NewProjectWizard — modal for creating a project under a program.
 *
 * Spec §11: persona is a "starter template," not a project property. The
 * wizard collects name + hypothesis + persona pick, calls
 * `workspaceStore.createProject`, then navigates into the new project.
 * The persona's `seedPrompt` is dispatched as a `workspace:composer-suggest`
 * window event after navigation so the chat surface's existing helper-
 * pick handler injects it into the composer.
 */

const SEED_DISPATCH_DELAY_MS = 250;

export const dispatchSeedPrompt = (text) => {
  if (typeof window === "undefined" || !text) return;
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("workspace:composer-suggest", { detail: { text } }));
  }, SEED_DISPATCH_DELAY_MS);
};

const TemplateCard = ({ template, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    className={`wiz__tmpl${selected ? " wiz__tmpl--selected" : ""}`}
    onClick={() => onSelect(template.id)}
    style={{ "--tmpl-color": template.color }}
    data-testid={`v2-wiz-tmpl-${template.id}`}
  >
    <span className="wiz__tmpl-icon" aria-hidden="true">{template.icon}</span>
    <span className="wiz__tmpl-label">{template.label}</span>
    <span className="wiz__tmpl-tag">{template.tagline}</span>
  </button>
);

export const NewProjectWizardBase = ({
  workspaceStore,
  open,
  onClose,
  programId,
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [hypothesis, setHypothesis] = useState("");
  const [templateId, setTemplateId] = useState(TEMPLATES[0]?.id || "none");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setName("");
    setHypothesis("");
    setTemplateId(TEMPLATES[0]?.id || "none");
    setSubmitting(false);
    setError(null);
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === "Escape" && !submitting) onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!programId) {
      setError("No program selected.");
      return;
    }
    if (!name.trim()) {
      setError("Project name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const project = await workspaceStore.createProject(programId, {
        name: name.trim(),
        hypothesis: hypothesis.trim(),
        // OpenAPI schema requires a string from the enum
        // [biology, chemistry, med-chem, none] — sending null fails
        // connexion validation with "None is not of type 'string'".
        // "none" is the sentinel for "no template".
        personaTemplate: templateId || "none",
      });
      const tmpl = TEMPLATES.find((t) => t.id === templateId);
      onClose?.();
      navigate(`/v2/p/${programId}/${project.id}`);
      if (tmpl?.seedPrompt) dispatchSeedPrompt(tmpl.seedPrompt);
    } catch (err) {
      setError(err?.message || String(err));
      setSubmitting(false);
    }
  }, [submitting, programId, name, hypothesis, templateId, workspaceStore, navigate, onClose]);

  if (!open) return null;

  return (
    <div className="wiz-root" data-testid="v2-new-project-wizard">
      <div
        className="wiz-backdrop"
        onClick={() => !submitting && onClose?.()}
        aria-hidden="true"
      />
      <div className="wiz-card" role="dialog" aria-label="New project">
        <header className="wiz-head">
          <h2 className="wiz-title">New project</h2>
          <button
            type="button"
            className="wiz-close"
            aria-label="Close"
            onClick={() => !submitting && onClose?.()}
            data-testid="v2-wiz-close"
          >
            ✕
          </button>
        </header>

        <form className="wiz-body" onSubmit={submit}>
          <label className="wiz-field">
            <span className="wiz-field__label">Name</span>
            <input
              type="text"
              className="wiz-field__input"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. G12C convergent vulnerabilities"
              data-testid="v2-wiz-name"
              disabled={submitting}
              maxLength={120}
            />
          </label>

          <label className="wiz-field">
            <span className="wiz-field__label">Hypothesis (optional)</span>
            <textarea
              className="wiz-field__textarea"
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="What's the working hypothesis for this project?"
              rows={2}
              data-testid="v2-wiz-hypothesis"
              disabled={submitting}
              maxLength={400}
            />
          </label>

          <fieldset className="wiz-field">
            <legend className="wiz-field__label">Quick-start template</legend>
            <div className="wiz-tmpls" role="radiogroup" data-testid="v2-wiz-tmpls">
              {TEMPLATES.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  selected={templateId === t.id}
                  onSelect={setTemplateId}
                />
              ))}
            </div>
            <p className="wiz-tmpl-hint">
              The template seeds your first chat with a starter prompt and suggested data sources.
              It's not a project property — Plex routes specialists based on what you actually ask.
            </p>
          </fieldset>

          {error && <div className="wiz-error" data-testid="v2-wiz-error">{error}</div>}

          <footer className="wiz-foot">
            <button
              type="button"
              className="wiz-btn wiz-btn--ghost button-ghost"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              data-testid="v2-wiz-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wiz-btn wiz-btn--primary button-primary"
              disabled={submitting || !name.trim()}
              data-testid="v2-wiz-submit"
            >
              {submitting ? "Creating…" : "Create project"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

const NewProjectWizard = inject("workspaceStore")(observer(NewProjectWizardBase));
export default NewProjectWizard;
